import React, { useEffect, useState } from 'react';

import { Web } from '@mui/icons-material';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AMCTable from './AMCTable';
import { getCSVAMC, getCSVMY } from './csv';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import { userHasAccess } from '../operations';
import AMCForm from './AMCForm';
import getEdgeReportingMenu from '../getEdgeReportingMenu';

import './styles.scoped.css';

const ClientBillingAMCMMYR = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const [data, setData] = useState([]);

  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [viewEnabled, setViewEnabled] = useState(false);

  const [isMasterCode, setIsMasterCode] = useState(true);
  const [masterCodeForm, setMasterCodeForm] = useState({
    FromPerEnddate: '',
    ToPerEnddate: '',
    AMC: '',
    BillToName: '',
    BillToNumber: 0,
    PayCode: '',
  });
  const [monthYearForm, setMonthYearForm] = useState({
    Year: '',
    AMC: '',
    BranchNumber: '',
    BillToName: '',
    BillToNumber: 0,
    LineOfBusiness: '',
    PayCode: '',
  });

  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingAmcCodes, setIsLoadingAmcCodes] = useState(false);
  const [isLoadingPayCodes, setIsLoadingPayCodes] = useState(false);
  const [isLoadingLOB, setIsLoadingLOB] = useState(false);

  useEffect(() => {
    if (menuItems) {
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) {
        navigate('/');
      }
    }
  }, [menuItems]);

  useEffect(() => {
    if (isMasterCode) {
      setViewEnabled(
        !!masterCodeForm?.FromPerEnddate &&
          !!masterCodeForm.ToPerEnddate &&
          (dayjs(masterCodeForm.FromPerEnddate).isBefore(masterCodeForm.ToPerEnddate) ||
            dayjs(masterCodeForm.FromPerEnddate).isSame(masterCodeForm.ToPerEnddate)),
      );
    } else {
      setViewEnabled(!!monthYearForm?.Year);
    }
  }, [masterCodeForm, monthYearForm, isMasterCode]);

  const fileName = `edge-reporting-client-billing-amc-${newDateFormatted.toString()}.csv`;

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Client Billing by AMC & MM/YY?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Client Billing by AMC & MM/YY'}
          midItemAction={() => setShowResults(false)}
          rightTitle={
            showResults
              ? `Viewing Report ${isMasterCode ? '(AMC Only)' : '(AMC MM/YY)'}`
              : 'Client Billing by AMC & MM/YY'
          }
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <CSVLink filename={fileName} data={isMasterCode ? getCSVAMC(data) : getCSVMY(data)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <AMCTable data={data} loading={loadingResults} isAMC={isMasterCode} />
          <div className="buttons-row">
            <Button variant="primary" onPress={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <AMCForm
              masterCodeForm={masterCodeForm}
              monthYearForm={monthYearForm}
              setMasterCodeForm={setMasterCodeForm}
              setMonthYearForm={setMonthYearForm}
              isLoadingAmcCodes={isLoadingAmcCodes}
              isLoadingBranches={isLoadingBranches}
              isLoadingPayCodes={isLoadingPayCodes}
              isLoadingLOB={isLoadingLOB}
              isMasterCode={isMasterCode}
              setIsMasterCode={setIsMasterCode}
              setIsLoadingBranches={setIsLoadingBranches}
              setIsLoadingAmcCodes={setIsLoadingAmcCodes}
              setIsLoadingPayCodes={setIsLoadingPayCodes}
              setIsLoadingLOB={setIsLoadingLOB}
              setLoadingResults={setLoadingResults}
              setData={setData}
              setShowResults={setShowResults}
              viewEnabled={viewEnabled}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default ClientBillingAMCMMYR;
